import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';
import { useAuth, AuthProvider } from './context/AuthContext';
import OnboardingTour from './components/OnboardingTour';

// Pages
import Dashboard        from './pages/Dashboard';
import Companies        from './pages/Companies';
import CompanySettings  from './pages/CompanySettings';
import Respondents      from './pages/Respondents';
import AssessmentForm   from './pages/AssessmentForm';
import Report           from './pages/Report';
import TrendDashboard   from './pages/TrendDashboard';
import Documents        from './pages/Documents';
import ImportAudit      from './pages/ImportAudit';
import Collaborators    from './pages/Collaborators';
import AIWorkspace      from './pages/AIWorkspace';
import Artifacts        from './pages/Artifacts';
import Billing          from './pages/Billing';
import Settings         from './pages/Settings';
import AdminDashboard   from './pages/admin/AdminDashboard';
import AdminUsers       from './pages/admin/AdminUsers';
import AdminCompanies   from './pages/admin/AdminCompanies';
import AdminSettings    from './pages/admin/AdminSettings';
import AdminQuestions   from './pages/admin/AdminQuestions';
import Login            from './pages/Login';
import Register         from './pages/Register';
import ForgotPassword   from './pages/ForgotPassword';
import ResetPassword    from './pages/ResetPassword';
import PublicAssess     from './pages/PublicAssess';

import './index.css';

// ── Page labels for topbar breadcrumb ────────────────────────────────────────
const PAGE_LABELS = {
  '/':                  'Dashboard',
  '/companies':         'Companies',
  '/settings/company':  'Company Settings',
  '/respondents':       'Respondents',
  '/assessment':        'Assessment',
  '/report':            'Report',
  '/trend':             'Trend Analysis',
  '/documents':         'Documents',
  '/imports':           'Import Audits',
  '/team':              'Team',
  '/ai':                'AI Workspace',
  '/billing':           'Billing',
  '/settings':          'Settings',
  '/artifacts':         'Artifact Repository',
  '/admin':             'Admin',
  '/admin/users':       'Users',
  '/admin/companies':   'Companies',
  '/admin/settings':    'Platform Settings',
  '/admin/questions':   'Question Library',
};

// ── Notification Bell ─────────────────────────────────────────────────────────
function NotificationBell({ authFetch }) {
  const navigate = useNavigate();
  const [notifs, setNotifs] = useState([]);
  const [open, setOpen]     = useState(false);
  const ref = React.useRef(null);

  const load = useCallback(() => {
    authFetch('/api/notifications').then(r => r.json()).then(d => {
      if (d && Array.isArray(d.notifications)) setNotifs(d.notifications.filter(n=>!n.read));
    }).catch(() => {});
  }, [authFetch]);

  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const markRead = async (n) => {
    setOpen(false);
    await authFetch(`/api/notifications/${n.id}/read`, { method: 'PUT' });
    setNotifs(prev => prev.filter(x => x.id !== n.id));
    if (n.link_page) {
      const pageRoutes = { report:'/report', respondents:'/respondents', artifacts:'/artifacts', team:'/team', billing:'/billing', ai:'/ai', companies:'/companies' };
      const route = pageRoutes[n.link_page] || '/';
      const data = n.link_data ? (typeof n.link_data === 'string' ? JSON.parse(n.link_data) : n.link_data) : {};
      navigate(route, { state: data });
    }
  };

  const markAllRead = async () => {
    await authFetch('/api/notifications/read-all', { method: 'PUT' });
    setNotifs([]);
  };

  const typeIcon = (type) => ({ success:'✅', warning:'⚠️', error:'❌', info:'ℹ️' }[type] || '🔔');
  const typeColor = (type) => ({ success:'var(--green)', warning:'var(--gold)', error:'var(--red)', info:'var(--accent)' }[type] || 'var(--text-3)');

  const timeAgo = (dt) => {
    const diff = Date.now() - new Date(dt + (dt.includes('Z')?'':'Z')).getTime();
    const m = Math.floor(diff/60000);
    if (m < 1) return 'just now';
    if (m < 60) return m + 'm ago';
    const h = Math.floor(m/60);
    if (h < 24) return h + 'h ago';
    return Math.floor(h/24) + 'd ago';
  };

  return (
    <div ref={ref} style={{ position:'relative' }}>
      <button
        className="topbar-btn"
        onClick={() => setOpen(o => !o)}
        style={{ background: open ? 'var(--bg-elevated)' : 'transparent', position:'relative' }}
      >
        🔔
        {notifs.length > 0 && (
          <span style={{
            position:'absolute', top:4, right:4, minWidth:16, height:16,
            background:'var(--red)', color:'#fff', borderRadius:10,
            fontSize:9, fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center',
            lineHeight:1, padding:'0 4px',
          }}>{notifs.length > 9 ? '9+' : notifs.length}</span>
        )}
      </button>
      {open && (
        <div style={{
          position:'absolute', top:'calc(100% + 8px)', right:0, zIndex:200,
          background:'var(--bg-surface)', border:'1px solid var(--border)',
          borderRadius:12, minWidth:320, maxWidth:380,
          boxShadow:'0 16px 48px rgba(0,0,0,0.25)', overflow:'hidden',
        }}>
          {/* Header */}
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'12px 16px', borderBottom:'1px solid var(--border)' }}>
            <div style={{ fontWeight:700, fontSize:13 }}>Notifications {notifs.length > 0 && <span style={{ background:'var(--blue-soft)', color:'var(--accent)', fontSize:11, borderRadius:10, padding:'1px 7px', marginLeft:6 }}>{notifs.length}</span>}</div>
            {notifs.length > 0 && <button onClick={markAllRead} style={{ background:'none', border:'none', fontSize:11, color:'var(--accent)', cursor:'pointer', fontWeight:600 }}>Mark all read</button>}
          </div>
          {/* List */}
          <div style={{ maxHeight:360, overflowY:'auto' }}>
            {notifs.length === 0 ? (
              <div style={{ padding:32, textAlign:'center', color:'var(--text-4)', fontSize:13 }}>
                <div style={{ fontSize:28, marginBottom:8 }}>🎉</div>
                You're all caught up!
              </div>
            ) : notifs.slice(0, 10).map(n => (
              <div key={n.id}
                onClick={() => markRead(n)}
                style={{
                  padding:'12px 16px', cursor:'pointer', borderBottom:'1px solid var(--border)',
                  display:'flex', gap:10, alignItems:'flex-start',
                  transition:'background 0.1s',
                  background: n.link_page ? 'transparent' : 'transparent',
                }}>
                <div style={{ fontSize:18, flexShrink:0, marginTop:1 }}>{typeIcon(n.type)}</div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:600, fontSize:12.5, color:'var(--text)', lineHeight:1.3, marginBottom:3 }}>{n.title}</div>
                  <div style={{ fontSize:12, color:'var(--text-3)', lineHeight:1.4 }}>{n.body}</div>
                  <div style={{ fontSize:10.5, color:'var(--text-4)', marginTop:4 }}>{timeAgo(n.created_at)}{n.link_page ? ' · tap to view →' : ''}</div>
                </div>
                <div style={{ width:6, height:6, borderRadius:'50%', background:typeColor(n.type), flexShrink:0, marginTop:6 }} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sidebar ───────────────────────────────────────────────────────────────────
function Sidebar({ darkMode, setDarkMode, mobileOpen, setMobileOpen }) {
  const { user, logout } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();
  const isAdmin   = user?.role === 'admin';
  const isPro     = user?.plan === 'pro' || user?.plan === 'enterprise';
  const activePath = location.pathname;

  const nav = [
    { path:'/',                icon:'◈',  label:'Dashboard' },
    { path:'/companies',       icon:'⊞',  label:'Companies',    tourKey:'companies' },
    { path:'/respondents',     icon:'👥', label:'Respondents',  tourKey:'respondents' },
    { path:'/report',          icon:'📊', label:'Report',       tourKey:'report' },
    { path:'/trend',           icon:'📈', label:'Trend',        pro:true },
    { path:'/documents',       icon:'📁', label:'Documents',    pro:true, tourKey:'documents' },
    { path:'/imports',         icon:'📥', label:'Import Audits',pro:true },
    { path:'/team',            icon:'🤝', label:'Team',         pro:true },
    { path:'/ai',              icon:'✦',  label:'AI Workspace', credits:true, tourKey:'ai' },
    { path:'/artifacts',       icon:'🗂',  label:'Artifacts',    tourKey:'artifacts' },
    { path:'/billing',         icon:'◎',  label:'Billing' },
    { path:'/settings',        icon:'⚙',  label:'Settings' },
  ];
  const adminNav = [
    { path:'/admin',              icon:'◉',  label:'Admin Dashboard' },
    { path:'/admin/users',        icon:'⊛',  label:'Users' },
    { path:'/admin/companies',    icon:'🏢', label:'Companies' },
    { path:'/admin/settings',     icon:'🚩', label:'Platform Settings' },
    { path:'/admin/questions',    icon:'📝', label:'Question Library' },
  ];

  const isActive = (path) =>
    path === '/' ? activePath === '/' : activePath.startsWith(path);

  // On mobile, tapping a nav item should also close the drawer
  const go = (path) => { navigate(path); setMobileOpen?.(false); };

  return (
    <>
      {/* Mobile drawer backdrop — tapping it closes the menu */}
      {mobileOpen && (
        <div className="sidebar-backdrop" onClick={() => setMobileOpen(false)} />
      )}

      <aside className={`sidebar ${mobileOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-logo">
          <div className="logo-mark">
            <div className="logo-icon">🧬</div>
            <div>
              <span className="logo-text-main">Enpower </span>
              <span className="logo-text-accent">Org DNA</span>
              <span className="logo-text-main">™</span>
            </div>
          </div>
          <div className="logo-tagline">Organizational Intelligence</div>
          {/* Close button — mobile drawer only */}
          <button className="sidebar-close-btn" onClick={() => setMobileOpen?.(false)} aria-label="Close menu">✕</button>
        </div>

        <nav className="sidebar-nav">
          <div className="nav-section">Workspace</div>
          {nav.map(item => (
            <div
              key={item.path}
              data-tour={item.tourKey ? `tour-${item.tourKey}` : undefined}
              className={`nav-item ${isActive(item.path) ? 'active' : ''}`}
              onClick={() => go(item.path)}
            >
              <span className="nav-icon">{item.icon}</span>
              {item.label}
              {item.credits && (
                <span className="credit-badge" style={{ marginLeft:'auto' }}>
                  <span>⚡</span><span>{user?.credits}</span>
                </span>
              )}
              {item.pro && !isPro && (
                <span style={{ marginLeft:'auto', fontSize:10, color:'var(--text-4)' }}>🔒</span>
              )}
            </div>
          ))}

          {isAdmin && (
            <>
              <div className="nav-section" style={{ marginTop:6 }}>Admin Console</div>
              {adminNav.map(item => (
                <div
                  key={item.path}
                  className={`nav-item ${isActive(item.path) ? 'active' : ''}`}
                  onClick={() => go(item.path)}
                >
                  <span className="nav-icon">{item.icon}</span>
                  {item.label}
                  {item.path === '/admin' && (
                    <span className="admin-sidebar-badge" style={{ marginLeft:'auto' }}>ADMIN</span>
                  )}
                </div>
              ))}
            </>
          )}
        </nav>

        <div className="sidebar-footer">
          <div className="theme-toggle" onClick={() => setDarkMode(d => !d)}>
            <span style={{ fontSize:12, fontWeight:500 }}>
              {darkMode ? '☀ Light mode' : '☾ Dark mode'}
            </span>
            <div className={`toggle-track ${darkMode ? 'on' : ''}`}>
              <div className="toggle-thumb" />
            </div>
          </div>
          <div className="user-chip" onClick={logout} title="Sign out">
            <div className="user-avatar">{user?.name?.[0]?.toUpperCase() || '?'}</div>
            <div style={{ flex:1, minWidth:0 }}>
              <div className="user-name">{user?.name}</div>
              <div className="user-plan">{user?.plan} plan · Sign out</div>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}

// ── Toast ─────────────────────────────────────────────────────────────────────
function Toast({ toast }) {
  if (!toast) return null;
  return (
    <div className={`toast toast-${toast.type || 'success'}`}>
      <span style={{
        fontSize:16, flexShrink:0,
        color: toast.type==='error' ? '#DC2626' : toast.type==='info' ? '#2563EB' : '#059669',
      }}>
        {toast.type==='error' ? '✕' : toast.type==='info' ? 'ℹ' : '✓'}
      </span>
      <span>{toast.msg}</span>
    </div>
  );
}

// ── useToast hook shared across all pages ─────────────────────────────────────
function useToast() {
  const [toast, setToast] = useState(null);
  const showToast = useCallback((msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }, []);
  return { toast, showToast };
}

// ── Shell: authenticated app layout ──────────────────────────────────────────
function AppShell() {
  const { user, authFetch } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();
  const { toast, showToast } = useToast();

  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved ? saved === 'dark' : window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showTour, setShowTour] = useState(
    () => localStorage.getItem('onboarding_complete') !== '1'
  );

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
    localStorage.setItem('theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  // Close the mobile drawer automatically whenever the route changes
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Shared props every page receives
  const pageProps = { showToast, user };
  const label = PAGE_LABELS[location.pathname] || 'Dashboard';

  return (
    <div className="app-shell">
      <Sidebar darkMode={darkMode} setDarkMode={setDarkMode} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />

      <main className="main-content">
        {/* Topbar */}
        <div className="topbar">
          <button className="hamburger-btn" onClick={() => setMobileOpen(o => !o)} aria-label="Open menu">
            <span/><span/><span/>
          </button>
          <div className="topbar-crumb">
            <span>Enpower</span>
            <span style={{ color:'var(--text-4)' }}>/</span>
            <b>{label}</b>
          </div>
          <div className="topbar-actions">
            <NotificationBell authFetch={authFetch} />
            <button className="topbar-btn" onClick={() => navigate('/settings/company')}>⚙ Settings</button>
            <button className="topbar-btn ai-btn" onClick={() => navigate('/ai')}>✦ AI Workspace</button>
          </div>
        </div>

        {/* Page content */}
        <div className="page-wrap">
          <Routes>
            <Route path="/"                 element={<Dashboard      {...pageProps} />} />
            <Route path="/companies"         element={<Companies      {...pageProps} />} />
            <Route path="/settings/company"  element={<CompanySettings {...pageProps} />} />
            <Route path="/respondents"       element={<Respondents    {...pageProps} />} />
            <Route path="/assessment"        element={<AssessmentForm  {...pageProps} />} />
            <Route path="/report"            element={<Report         {...pageProps} />} />
            <Route path="/trend"             element={<TrendDashboard  {...pageProps} />} />
            <Route path="/documents"         element={<Documents      {...pageProps} />} />
            <Route path="/imports"           element={<ImportAudit    {...pageProps} />} />
            <Route path="/team"              element={<Collaborators  {...pageProps} />} />
            <Route path="/ai"               element={<AIWorkspace    {...pageProps} />} />
            <Route path="/artifacts"         element={<Artifacts      {...pageProps} />} />
            <Route path="/billing"           element={<Billing        {...pageProps} />} />
            <Route path="/settings"          element={<Settings       {...pageProps} />} />
            <Route path="/admin"             element={<AdminDashboard  {...pageProps} />} />
            <Route path="/admin/users"       element={<AdminUsers     {...pageProps} />} />
            <Route path="/admin/companies"   element={<AdminCompanies  {...pageProps} />} />
            <Route path="/admin/settings"    element={<AdminSettings  {...pageProps} />} />
            <Route path="/admin/questions"   element={<AdminQuestions  {...pageProps} />} />
            <Route path="*"                 element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </main>

      <Toast toast={toast} />

      {showTour && user && (
        <OnboardingTour onComplete={() => setShowTour(false)} setMobileOpen={setMobileOpen} />
      )}
    </div>
  );
}

// ── Accept Invite ─────────────────────────────────────────────────────────────
function AcceptInvite() {
  const { user, authFetch } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const token    = new URLSearchParams(location.search).get('token');
  const [info, setInfo]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [done, setDone]       = useState(false);
  const [err, setErr]         = useState('');

  useEffect(() => {
    if (!token) { setErr('Invalid invite link'); setLoading(false); return; }
    fetch(`/api/collaborators/invite-info?token=${token}`)
      .then(r => r.json()).then(d => { setInfo(d); setLoading(false); })
      .catch(() => { setErr('Failed to load invite'); setLoading(false); });
  }, [token]);

  const accept = async () => {
    if (!user) { navigate('/login'); return; }
    const res = await authFetch('/api/collaborators/accept', {
      method:'POST', body: JSON.stringify({ token })
    });
    const d = await res.json();
    if (res.ok) {
      setDone(true);
      // Redirect to dashboard so the collaborator sees the shared company in their list
      setTimeout(() => navigate('/', { state: { justAccepted: true, companyId: d.company_id } }), 1500);
    } else { setErr(d.error || 'Failed to accept invite'); }
  };

  if (loading) return <div className="page-loading"><div className="loader" /></div>;

  return (
    <div className="auth-wrap">
      <div className="auth-card">
        <div className="auth-logo">
          <div className="auth-logo-icon">🤝</div>
        </div>
        <div className="auth-title">{done ? 'You\'re in!' : 'Team Invitation'}</div>
        {err ? (
          <div className="form-error">{err}</div>
        ) : done ? (
          <p className="auth-sub">You now have access to <strong>{info?.company_name}</strong>. Taking you to your dashboard…</p>
        ) : (
          <>
            <p className="auth-sub">
              You've been invited to join <strong>{info?.company_name}</strong> as a {info?.role}.
            </p>
            <div style={{ display:'flex', gap:8 }}>
              <button className="btn btn-primary" style={{ flex:1 }} onClick={accept}>Accept</button>
              <button className="btn btn-ghost" style={{ flex:1 }} onClick={() => navigate('/login')}>
                {user ? 'Cancel' : 'Log In first'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Root: routing tree ────────────────────────────────────────────────────────
function AppInner() {
  const { user, loading } = useAuth();

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh' }}>
      <div className="loader" />
    </div>
  );

  return (
    <Routes>
      {/* Public routes — always accessible */}
      <Route path="/assess/:companyId"    element={<PublicAssessWrapper />} />
      <Route path="/reset-password"       element={<ResetPasswordWrapper />} />
      <Route path="/accept-invite"        element={<AcceptInvite />} />

      {/* Auth routes — only when NOT logged in */}
      <Route path="/login"          element={!user ? <LoginWrapper />         : <Navigate to="/" replace />} />
      <Route path="/register"       element={!user ? <RegisterWrapper />      : <Navigate to="/" replace />} />
      <Route path="/forgot-password"element={!user ? <ForgotPasswordWrapper />: <Navigate to="/" replace />} />

      {/* Protected routes — need login */}
      <Route path="/*" element={user ? <AppShell /> : <Navigate to="/login" replace />} />
    </Routes>
  );
}

// ── Wrappers (pass showToast/navigate to pages that need them) ───────────────
function PublicAssessWrapper() {
  const { companyId } = useParams();
  const [toast, setToast] = useState(null);
  const showToast = (msg, type='success') => { setToast({msg,type}); setTimeout(()=>setToast(null),3500); };
  return <><PublicAssess companyId={companyId} showToast={showToast} /><Toast toast={toast} /></>;
}
function ResetPasswordWrapper() {
  const navigate = useNavigate();
  const [toast, setToast] = useState(null);
  const showToast = (msg, type='success') => { setToast({msg,type}); setTimeout(()=>setToast(null),3500); };
  return <><ResetPassword navigate={navigate} showToast={showToast} /><Toast toast={toast} /></>;
}
function LoginWrapper() {
  const navigate = useNavigate();
  const [toast, setToast] = useState(null);
  const showToast = (msg, type='success') => { setToast({msg,type}); setTimeout(()=>setToast(null),3500); };
  return <><Login navigate={navigate} showToast={showToast} /><Toast toast={toast} /></>;
}
function RegisterWrapper() {
  const navigate = useNavigate();
  const [toast, setToast] = useState(null);
  const showToast = (msg, type='success') => { setToast({msg,type}); setTimeout(()=>setToast(null),3500); };
  return <><Register navigate={navigate} showToast={showToast} /><Toast toast={toast} /></>;
}
function ForgotPasswordWrapper() {
  const navigate = useNavigate();
  return <ForgotPassword navigate={navigate} />;
}

// ── App root ──────────────────────────────────────────────────────────────────
export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppInner />
      </AuthProvider>
    </BrowserRouter>
  );
}
